//
// # Symbol Content
//

// Local Imports
import { logger, Result, Ok, exhaust } from "CoreUtils";
import { model, schdata, graphics } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import { EntityKind } from "DrawCore/src/entity";
import {
  ExprLabel,
  SymbolPin,
  SymbolPinShape,
  SymbolPinLabel,
  Annotation,
} from "DrawCore/src/content/shared";
import { Annotations } from "../shared/annotations";
import type { InteractionHost } from "../../interaction_host";
import * as changes from "DrawCore/src/changes";
import { MousePos } from "DrawCore/src/mousepos";
import * as modes from "../../modes";
import { Keys } from "../../keys";
import { RectCorner, RectEntity } from "DrawCore/src/primitives/rect";
import {
  SelectionBoxEntity,
  SelectionBoxCorner,
} from "DrawCore/src/content/symbol/selectionbox";
import { PathVertex, PathEntity } from "DrawCore/src/primitives/path";
import { PolygonVertex, PolygonEntity } from "DrawCore/src/primitives/polygon";
import { EllipseEntity, EllipseCorner } from "DrawCore/src/primitives/ellipse";
import { ArcEntity, ArcCorner } from "DrawCore/src/primitives/arc";
import { TextEntity } from "DrawCore/src/primitives/text";
import { ContentKind } from "DrawCore/src/content/kind";
import { SymbolRender, type Child } from "DrawCore/src/content/symbol/render";
import { InteractionBase } from "../interaction_base";
import { StyleTables } from "DrawCore/src/content/shared/styles";
import { AddSymbolPin, AddSelectionBox } from "./modes";

type Selectable =
  | SymbolPinShape
  | SymbolPinLabel
  | ExprLabel
  | RectCorner
  | RectEntity
  | SelectionBoxEntity
  | SelectionBoxCorner
  | PathVertex
  | PathEntity
  | PolygonVertex
  | PolygonEntity
  | EllipseEntity
  | EllipseCorner
  | ArcEntity
  | ArcCorner
  | TextEntity;

//
// # Symbol Content — interaction tier
//
export class Symbol extends InteractionBase<Child, Selectable, SymbolRender> {
  override readonly contentKind = ContentKind.Symbol;

  constructor(editor: InteractionHost) {
    super(editor, new SymbolRender(editor));
  }

  // Annotation editing over the render tier's graphics.
  readonly annotations = new Annotations(
    this,
    StyleTables.symbol,
    this.render.annotations
  );

  // Content-specific UI state
  lastSymbolPinData: schdata.SymbolPin = schdata.SymbolPin.default();

  // Enter the two-phase draw mode for adding a new selection box.
  startAddSelectionBox = (): unknown => {
    return AddSelectionBox.enter(this);
  };

  // Apply a `Change`, generally as part of an undo or redo operation.
  override applyChange = (change: changes.Change<Child>): unknown => {
    switch (change.kind) {
      case changes.Kind.Add: {
        return this.addEntity(change.entity, false);
      }
      case changes.Kind.Remove: {
        return this.removeEntity(change.entity, false);
      }
      case changes.Kind.Update: {
        return change.apply(); // Reminder when undo'ing, this is the *inverse* change! Always `apply`.
      }
      case changes.Kind.Combo: {
        change.changes.forEach(this.applyChange);
        return;
      }
      default:
        exhaust(change); // Exhaustiveness check
    }
  };

  override addEntity = (entity: Child, logChange: boolean = true): unknown => {
    // FIXME: LOG CHANGES
    const { entityKind } = entity;
    switch (entityKind) {
      case EntityKind.SymbolPin:
        return this.render.pins.add(entity);
      case EntityKind.Annotation:
        return this.annotations.add(entity);
      case EntityKind.ExprLabel:
        return this.render.exprLabels.add(entity);
      case EntityKind.SelectionBox:
        return this.render.selectionBoxEntitys.add(entity);
      default:
        exhaust(entityKind);
    }
  };
  override removeEntity = (
    entity: Child,
    logChange: boolean = true
  ): boolean => {
    // FIXME: LOG CHANGES
    // FIXME: Once Annotations have IDs, extract `id` from `entity.data` at the top
    // and remove the per-branch ID checks.
    const { entityKind } = entity;
    switch (entityKind) {
      case EntityKind.SymbolPin: {
        const { id } = entity.data;
        if (id === undefined) {
          logger.warn("Cannot remove SymbolPin without an ID");
          return false;
        }
        return this.render.pins.remove(id);
      }
      case EntityKind.Annotation:
        return this.annotations.remove(entity);
      case EntityKind.ExprLabel: {
        const { id } = entity.data;
        if (id === undefined) {
          logger.warn("Cannot remove ExprLabel without an ID");
          return false;
        }
        return this.render.exprLabels.remove(id);
      }
      case EntityKind.SelectionBox: {
        const { id } = entity.data;
        if (id === undefined) {
          logger.warn("Cannot remove SelectionBox without an ID");
          return false;
        }
        return this.render.selectionBoxEntitys.remove(id);
      }
      default:
        exhaust(entityKind);
    }
  };

  private whatdWeHit_ = (mousePos: MousePos): Selectable | null => {
    // Test pin labels and shapes
    for (const pin of this.render.pins.values()) {
      const { labelRef, shapeRef } = pin;
      if (labelRef && labelRef.hitTest(mousePos)) {
        return labelRef;
      }
      if (shapeRef.hitTest(mousePos)) {
        return shapeRef;
      }
    }
    // Test expression labels
    for (const exprLabel of this.render.exprLabels.values()) {
      if (exprLabel.hitTest(mousePos)) return exprLabel;
    }
    // Selection boxes: test corners before edges so resizing takes priority.
    for (const box of this.render.selectionBoxEntitys.values()) {
      for (const corner of box.corners) {
        if (corner.hitTest(mousePos)) return corner;
      }
    }
    for (const box of this.render.selectionBoxEntitys.values()) {
      if (box.edgeHitTest(mousePos)) return box;
    }
    // Rectangles: test corners before edges so resizing takes priority.
    for (const rect of this.annotations.rects) {
      for (const corner of rect.corners) {
        if (corner.hitTest(mousePos)) {
          return corner;
        }
      }
    }
    // Ellipses & arcs: corners (resize) take priority over the body/curve (move).
    for (const ellipse of this.annotations.ellipses) {
      for (const corner of ellipse.corners) {
        if (corner.hitTest(mousePos)) {
          return corner;
        }
      }
    }
    for (const arc of this.annotations.arcs) {
      for (const corner of arc.corners) {
        if (corner.hitTest(mousePos)) {
          return corner;
        }
      }
    }

    // Test graphics element edges for whole-shape movement.
    for (const rect of this.annotations.rects) {
      if (rect.edgeHitTest(mousePos)) {
        return rect;
      }
    }
    for (const polygon of this.annotations.polygons) {
      if (polygon.edgeHitTest(mousePos)) {
        return polygon;
      }
    }
    for (const path of this.annotations.paths) {
      if (path.edgeHitTest(mousePos)) {
        return path;
      }
    }
    for (const ellipse of this.annotations.ellipses) {
      if (ellipse.body.hitTest(mousePos)) {
        return ellipse;
      }
    }
    for (const arc of this.annotations.arcs) {
      if (arc.hitTest(mousePos)) {
        return arc;
      }
    }
    for (const text of this.annotations.texts) {
      if (text.hitTest(mousePos)) {
        return text;
      }
    }

    // Polygons and paths: edges take priority over vertices,
    // so shapes move as a unit by default.
    for (const polygon of this.annotations.polygons) {
      for (const vertex of polygon.vertices) {
        if (vertex.hitTest(mousePos)) {
          return vertex;
        }
      }
    }
    for (const path of this.annotations.paths) {
      for (const vertex of path.vertices) {
        if (vertex.hitTest(mousePos)) {
          return vertex;
        }
      }
    }

    return null; // Missed everything
  };

  // Hit Testing
  override whatdWeHit = (mousePos: MousePos): Selectable | null => {
    const weHit = this.whatdWeHit_(mousePos); // Call the inner implementation
    return weHit; // Place for a very common breakpoint
  };

  // Create a (drawn) `Symbol` from the abstract data model
  static fromSchema(
    editor: InteractionHost,
    data: schdata.SymbolJson
  ): Result<Symbol> {
    const symbolData = schdata.Symbol.fromJson(data);
    const symbol = Symbol.fromData(editor, symbolData);

    // Copy PDK identifier
    if (data.pdkId) {
      symbol.render.pdkId = data.pdkId;
    }

    // Copy the forward references to schematics that implement this symbol
    if (data.schematicImpls) {
      symbol.render.schematicImpls = data.schematicImpls;
    }

    return Ok(symbol);
  }

  // Create a (drawn) `Symbol` from the abstract data model
  static fromData(editor: InteractionHost, data: schdata.Symbol): Symbol {
    // Create the result `Symbol` and build entities under its render tier.
    const symbol = new Symbol(editor);
    const { render } = symbol;
    symbol.ident = data.ident;

    // Add all its graphics elements and register any groups
    data.graphics.forEach((element) => {
      render.annotations.graphicsEntity.add(element);
      render.annotations.registerGroup(element);
    });

    // Set up its bounding box
    render.bbox = schdata.Bbox.clone(data.bbox ?? schdata.Bbox.empty());

    // Create its selection boxes
    data.selectionBoxes.map((bbox) => {
      const boxEntity = new SelectionBoxEntity({
        data: graphics.Rectangle.fromPartial({ bbox, id: bbox.id }),
        parent: render,
        table: StyleTables.symbolSelectionBox,
      });
      render.selectionBoxEntitys.add(boxEntity);
    });

    // Create its pins
    data.pins.map((pin) => {
      const pinDrawing = new SymbolPin({ data: pin, parent: render });
      render.pins.add(pinDrawing);
    });

    // Create its labels
    data.exprLabels.map((labeldata) => {
      const exprLabel = new ExprLabel({ data: labeldata, parent: render });
      render.exprLabels.add(exprLabel);
    });

    return symbol;
  }

  toData = (): schdata.Symbol => {
    const { render } = this;
    const data = schdata.Symbol.fromPartial({ ident: this.ident });
    data.bbox = schdata.Bbox.clone(render.bbox);
    data.graphics = render.annotations.graphicsEntity.toData();
    data.pins = Array.from(render.pins.values()).map((pin) => pin.data);
    data.exprLabels = Array.from(render.exprLabels.values()).map(
      (label) => label.data
    );
    data.selectionBoxes = Array.from(render.selectionBoxEntitys.values()).map(
      (box) => ({ ...box.data.bbox, id: box.data.id })
    );
    return data;
  };

  override idleMode = (): modes.ModeHandler => new SymbolIdle(this);

  static createNew(editor: InteractionHost): Symbol {
    return Symbol.fromData(
      editor,
      schdata.Symbol.fromPartial({
        ident: { kind: "hierpath" as const, path: [] },
      })
    );
  }
}

// # Idle Mode
//
// The typical state of sitting there, doing nothing.
// Primarily this state waits for actions to enter other modes.
// Clicking on entities selects them.
// Note having something `selected` is orthogonal to `Idle`;
// we can be in this state and have something selected.
//
export class SymbolIdle extends modes.ModeHandler {
  constructor(readonly symbol: Symbol) {
    super();
  }
  get editor() {
    return this.symbol.editor;
  }

  // On `abort`, just de-select anything highlighted.
  override abort = () => this.editor.content.deselect();

  // In idle mode, if we clicked on something, react and update our UI state.
  override handleMouseDown = (e: MouseEvent) => {
    const { editor, symbol } = this;

    // If a context menu is open, this click is dismissing it — don't start any interaction.
    if (editor.isAnyContextMenuVisible()) {
      return;
    }

    // Middle click: ALWAYS start panning, regardless of what's under cursor
    if (e.button === 1) {
      editor.modeHandler = modes.Pan.start(editor);
      return;
    }

    // Hit test, finding which element was clicked on.
    const whatd_we_hit = symbol.whatdWeHit(editor.uiState.mousePos);

    if (!whatd_we_hit) {
      // Right-click on empty space: show context menu
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        // Get the grid-snapped position for entity creation
        const gridPoint = editor.gridPointNearestToMouse();
        editor.emptySpaceContextMenu.show(
          e.pageX,
          e.pageY,
          gridPoint.x,
          gridPoint.y
        );
        return;
      }

      // Command-click: start panning.
      if (e.metaKey) {
        editor.modeHandler = modes.Pan.start(editor);
        return;
      }

      // Hit "blank space". De-select whatever we've got.
      return editor.deselect();
    }
    // Resolve the "selectable parent" entity for the hit target.
    // Some hit targets (e.g. pin shapes) resolve to their parent for selection.
    const { entityKind } = whatd_we_hit;
    let selectTarget: Child;
    switch (entityKind) {
      case EntityKind.SymbolPinShape:
      case EntityKind.SymbolPinLabel: {
        selectTarget = whatd_we_hit.parent as SymbolPin;
        break;
      }
      // Selection boxes are first-class Child entities (not wrapped in Annotation).
      case EntityKind.SelectionBox: {
        selectTarget = whatd_we_hit;
        break;
      }
      // Whole-shape graphics elements (hit via edge/body detection).
      // The primitive IS the whatd_we_hit — wrap it as an Annotation for selection.
      case EntityKind.Rect:
      case EntityKind.Polygon:
      case EntityKind.Path:
      case EntityKind.Ellipse:
      case EntityKind.Arc:
      case EntityKind.Text: {
        selectTarget = new Annotation(whatd_we_hit);
        break;
      }
      // Vertex/corner handles for fine-grained editing.
      // Their .parent is the primitive — wrap that as an Annotation for selection.
      case EntityKind.PolygonVertex:
      case EntityKind.PathVertex:
      case EntityKind.RectCorner:
      case EntityKind.EllipseCorner:
      case EntityKind.ArcCorner: {
        selectTarget = new Annotation(whatd_we_hit.parent);
        break;
      }
      // Selection box corner: select the parent selection box entity.
      case EntityKind.SelectionBoxCorner: {
        selectTarget = (whatd_we_hit as SelectionBoxCorner).parent;
        break;
      }
      case EntityKind.ExprLabel: {
        selectTarget = whatd_we_hit;
        break;
      }
      default:
        exhaust(entityKind);
        return;
    }

    // Right-click on an entity: show context menu.
    if (e.button === 2) {
      e.preventDefault();
      e.stopPropagation();
      symbol.select(selectTarget);
      if (
        entityKind === EntityKind.SymbolPinShape ||
        entityKind === EntityKind.SymbolPinLabel
      ) {
        const pin = whatd_we_hit.parent as SymbolPin;
        editor.symbolPinContextMenu.show(pin.data, e.pageX, e.pageY);
      } else if (entityKind === EntityKind.ExprLabel) {
        editor.symbolExprLabelContextMenu.show(whatd_we_hit, e.pageX, e.pageY);
      }
      return;
    }

    // Shift+click: toggle the entity in the multi-selection set.
    if (e.shiftKey) {
      return symbol.addToSelection(selectTarget);
    }

    // Select and start moving.
    // Whole-shape graphics primitives go through annotations.startMove (handles
    // groups). Whole selection boxes translate via MoveGraphics (full data).
    // Handles whose position lives in the owning entity's geometry (pin
    // shapes/labels, graphics vertices/corners, selection-box corners) drag
    // live via the handle but log a full-data change against the owner, so the
    // edit round-trips and marks the design dirty (see MoveHandle). Standalone
    // expression labels have a `loc` field, so plain Move suffices.
    switch (entityKind) {
      case EntityKind.Rect:
      case EntityKind.Polygon:
      case EntityKind.Path:
      case EntityKind.Ellipse:
      case EntityKind.Arc:
      case EntityKind.Text:
        return symbol.annotations.startMove(whatd_we_hit);
      case EntityKind.SelectionBox:
        symbol.select(selectTarget);
        return modes.MoveGraphics.enter(editor, whatd_we_hit);
      case EntityKind.SymbolPinShape:
      case EntityKind.SymbolPinLabel:
      case EntityKind.RectCorner:
      case EntityKind.PolygonVertex:
      case EntityKind.PathVertex:
      case EntityKind.EllipseCorner:
      case EntityKind.ArcCorner:
      case EntityKind.SelectionBoxCorner:
        symbol.select(selectTarget);
        return modes.MoveHandle.enter(
          editor,
          whatd_we_hit,
          whatd_we_hit.parent
        );
      default:
        symbol.select(selectTarget);
        return modes.Move.enter(editor, whatd_we_hit);
    }
  };
  // Handle keystrokes.
  override handleKey = (e: KeyboardEvent) => {
    const { editor, symbol } = this;

    // All other UI states: check for "command" keystrokes.
    switch (e.key) {
      case Keys.Delete:
      case Keys.Backspace: {
        const count = symbol.selection.size;
        const result = symbol.deleteSelected();
        if (result) editor.showItemFeedback("Deleted", count);
        return result;
      }

      // Mode-Changing Command Keys
      case Keys.p:
        return AddSymbolPin.enter(symbol);
      case Keys.e: // FIXME: why e? Why not?
        return symbol.annotations.startAddRect();
      case "y": // FIXME: why y? Why not?
        return symbol.annotations.startAddPolygon();
      case "u": // FIXME: why u? Why not?
        return symbol.annotations.startAddPath();
      // Shift+E / Shift+C for the two new primitives. Plain mnemonic letters
      // (o, c, a, b) all collide with existing symbol-scoped bindings
      // (pin-direction O/X, box-select B, etc.), so we use shifted variants.
      case "E": // Shift+E — Ellipse
        return symbol.annotations.startAddEllipse();
      case "C": // Shift+C — arC
        return symbol.annotations.startAddArc();

      // Rotation & refelection
      // Note these are versions of rotation & reflection which *are*
      // added to the undo/redo changelog.
      // case Keys.r:
      //   return symbol.rotateSelected();
      // case Keys.v:
      //   return symbol.flipSelected(Direction.Vert);
      // case Keys.h:
      //   return symbol.flipSelected(Direction.Horiz);
      default:
        // Note this *is not* an exhaustive check, on purpose.
        // There's lots of keys we don't care about!
        // Some day the logging should go away too.
        logger.log(`Key we dont use: '${e.key}'`);
    }
  };
}

// Compute label TextFormat for a symbol pin at the given center, based on label position.
// Pin shape is 10x10 (half-size 5 from center). Offsets are from the pin center.
// Accounts for schematic y-up coordinates and canvas y-axis inversion.
export function pinLabelFmt(
  center: { x: number; y: number },
  labelPosition?: string
): model.text.TextFormat {
  const labelOffsetHoriz = 10; // 5 (pin edge) + 5 padding
  const labelOffsetVert = 8; // 5 (pin edge) + 3 padding
  let loc: { x: number; y: number };
  let alignment: "left" | "center" | "right";
  let baseline: "top" | "middle" | "bottom";
  switch (labelPosition) {
    case "right":
      loc = { x: center.x + labelOffsetHoriz, y: center.y };
      alignment = "left";
      baseline = "middle";
      break;
    case "above":
      // +y is up in schematic coordinates; "bottom" baseline so text extends upward from anchor
      loc = { x: center.x, y: center.y + labelOffsetVert };
      alignment = "center";
      baseline = "bottom";
      break;
    case "below":
      // -y is down in schematic coordinates; "top" baseline so text extends downward from anchor
      loc = { x: center.x, y: center.y - labelOffsetVert };
      alignment = "center";
      baseline = "top";
      break;
    default:
      // "left" (default)
      loc = { x: center.x - labelOffsetHoriz, y: center.y };
      alignment = "right";
      baseline = "middle";
      break;
  }
  return model.text.TextFormat.fromPartial({
    loc,
    alignment,
    baseline,
    height: 16,
  });
}

// Infer the label position from a TextFormat's alignment and baseline.
// This is the exact inverse of pinLabelFmt.
export function inferLabelPosition(fmt: model.text.TextFormat): string {
  if (fmt.alignment === "left" && fmt.baseline === "middle") return "right";
  if (fmt.alignment === "center" && fmt.baseline === "bottom") return "above";
  if (fmt.alignment === "center" && fmt.baseline === "top") return "below";
  return "left"; // alignment "right" + baseline "middle", or any other combination
}
